import React from 'react';
import { Link } from 'react-router-dom';
import { Laptop, BarChart3, FileText, Users, Shield, Zap, CheckCircle2 } from 'lucide-react';

const TechnologyPlatformPage = () => {
  const tools = [
    {
      icon: BarChart3,
      title: "Quote Comparison Engine",
      description: "Instantly compare rates from 13+ carriers with our real-time quoting system",
      features: ["Side-by-side comparisons", "Multiple product types", "Carrier illustrations"]
    },
    {
      icon: Users,
      title: "Client Management (CRM)",
      description: "Track clients, prospects, and policies all in one place",
      features: ["Contact management", "Policy tracking", "Task reminders"]
    },
    {
      icon: FileText,
      title: "Application Processing",
      description: "Streamlined application submission and tracking",
      features: ["E-applications", "Status tracking", "Document management"]
    },
    {
      icon: Laptop,
      title: "Training Portal",
      description: "On-demand access to training materials and certifications",
      features: ["Video courses", "Product training", "Compliance education"]
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="technology-platform-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Technology Platform
            </h1>
            <p className="text-slate-300 text-xl">
              Modern tools and technology to help you serve clients efficiently and grow your practice.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tools.map((tool, idx) => {
              const Icon = tool.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#C5A059]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-xl mb-2">{tool.title}</h3>
                      <p className="text-slate-400 text-sm">{tool.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {tool.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#C5A059]" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Built for Modern Advisors</h2>
              <p className="text-slate-400 mb-6">
                Our technology platform is designed to help you work smarter, not harder. Access everything you need from any device, anywhere.
              </p>
              <ul className="space-y-4">
                {[
                  "Cloud-based — access from anywhere",
                  "Mobile-friendly for on-the-go work",
                  "Regular updates with new features",
                  "Dedicated support team",
                  "Carrier integrations for seamless processing"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8 text-center">
              <Zap className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
              <h3 className="text-white font-semibold text-xl mb-4">Get Started Quickly</h3>
              <p className="text-slate-400 text-sm mb-6">
                New advisors get full access to our technology platform from day one. Training is included to help you maximize these tools.
              </p>
              <Link
                to="/become-an-agent"
                className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-6 py-3 uppercase tracking-wider text-sm transition-all"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyPlatformPage;
